// src/components/messaging/MessageCenter.jsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useMessagingStore } from '../../state/messagingStore';
import { useProfileStore } from '../../state/profileStore';
import { useMatrixStore } from '../../state/matrixStore';
import { useModalStore } from '../../state/modalStore';
import { useConversationStore } from '../../state/conversationStore';
import { Rnd } from 'react-rnd';
import ThreadList from './ThreadList';
import useMatrixClient from '../../hooks/useMatrixClient';

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Discover People panel — shown in the right pane when no thread is selected ──
function DiscoverPeoplePanel() {
  const profile = useProfileStore((s) => s.profile);
  const openCandidate = useModalStore((s) => s.openCandidate);
  const openWithCandidate = useMatrixStore((s) => s.openWithCandidate);
  const openChatCenter = useMatrixStore((s) => s.open);
  const setSelectedThread = useMessagingStore((s) => s.setSelectedThread);
  const createLocalInviteThread = useMessagingStore((s) => s.createLocalInviteThread);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  // Fetch nearby users on mount
  const fetchUsers = useCallback(async () => {
    if (!profile?.u_id) return;
    const lat = profile.b_lat || profile.lat;
    const lng = profile.b_lng || profile.lng;
    if (!lat || !lng) return;

    setLoading(true);
    try {
      // Fetch ALL users within 100 miles (no focus filter) — we sort client-side
      const res = await fetch(
        `${API_URL}/api/profile/teams/local-users?focus=ALL&lat=${lat}&lng=${lng}&radius=100`,
        { credentials: 'include' }
      );
      const data = await res.json();
      if (Array.isArray(data)) setUsers(data);
    } catch (err) {
      console.error('[DiscoverPeople] fetch error:', err);
    }
    setLoading(false);
  }, [profile]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const myFocus = (profile?.u_customer_focus || '').toUpperCase();

  // Filter by search query, then sort: same customer focus first, then others — both by distance
  const filteredAndSorted = (() => {
    let list = users;
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(u => {
        const name = `${u.u_first_name || ''} ${u.u_last_name || ''}`.toLowerCase();
        const biz = (u.b_name || '').toLowerCase();
        const ind = (u.b_industry || '').toLowerCase();
        return name.includes(q) || biz.includes(q) || ind.includes(q);
      });
    }
    // Sort: same focus first (by distance), then others (by distance)
    return [...list].sort((a, b) => {
      const aMatch = (a.u_customer_focus || '').toUpperCase() === myFocus ? 0 : 1;
      const bMatch = (b.u_customer_focus || '').toUpperCase() === myFocus ? 0 : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
      return (a.distance_mi || 999) - (b.distance_mi || 999);
    });
  })();

  const filtered = filteredAndSorted;

  const myUid = Number(profile?.u_id);

  const handleStartChat = async (user) => {
    const candidateId = Number(user.u_id);
    if (!Number.isFinite(myUid) || !Number.isFinite(candidateId)) return;

    const a = Math.min(myUid, candidateId);
    const b = Math.max(myUid, candidateId);
    const aliasFull = `#eh.dm.u${a}_u${b}:ehandshake.com`;

    const displayName = [user.u_first_name, user.u_last_name].filter(Boolean).join(' ') || 'User';
    const profileImg = user.u_profile_picture || '/images/default-avatar.png';
    const businessName = user.b_name || '';

    // Load conversations to check for existing DM
    try {
      await useConversationStore.getState().loadConversations();
    } catch {}

    // Create local thread if it doesn't exist
    const msgState = useMessagingStore.getState();
    const existingThread = (msgState.optimisticThreads || []).find((t) => {
      return String(t.alias || '') === aliasFull || Number(t.peerUid) === candidateId;
    });

    if (!existingThread) {
      createLocalInviteThread({
        candidateId,
        candidateName: displayName,
        candidateAvatar: profileImg,
        candidateBusiness: businessName,
      });
    }

    setSelectedThread(aliasFull);
    openWithCandidate({
      candidateId,
      candidateName: displayName,
      candidateAvatar: profileImg,
      candidateBusiness: businessName,
    });
  };

  const handleViewProfile = (user) => {
    openCandidate({
      candidate: user,
      viewerUId: myUid,
      top: 90,
      right: 30,
      offsetRight: 0,
    });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="shrink-0 px-4 py-3 border-b border-gray-200 bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Discover People Nearby</h3>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, business, or industry…"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00aef7]/30 focus:border-[#00aef7]"
        />
      </div>

      {/* User list */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex items-center gap-3 p-3 animate-pulse">
                <div className="w-11 h-11 rounded-full bg-gray-200" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-1.5" />
                  <div className="h-3 bg-gray-100 rounded w-3/4" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-400">
            {query.trim() ? 'No matches found.' : 'No nearby professionals found.'}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filtered.map(user => {
              const name = [user.u_first_name, user.u_last_name].filter(Boolean).join(' ') || 'Unknown';
              const dist = user.distance_mi != null ? `${Math.round(user.distance_mi)} mi` : '';
              const photo = user.u_profile_picture || null;
              const initials = [user.u_first_name?.[0], user.u_last_name?.[0]].filter(Boolean).join('').toUpperCase();
              const userFocus = (user.u_customer_focus || '').toUpperCase();
              const focusMatch = userFocus === myFocus;

              return (
                <div
                  key={user.u_id}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-[#00aef7]/5 transition-colors group"
                >
                  {/* Avatar */}
                  {photo ? (
                    <img src={photo} alt="" className="w-11 h-11 rounded-full object-cover border border-gray-200 shrink-0" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#00aef7] to-[#0095d0] flex items-center justify-center text-white text-sm font-bold shrink-0">
                      {initials || '?'}
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-gray-800 truncate">{name}</span>
                      {userFocus && (
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${
                          focusMatch
                            ? 'bg-green-100 text-green-700 border border-green-200'
                            : 'bg-gray-100 text-gray-500 border border-gray-200'
                        }`}>
                          {userFocus}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{user.b_name || 'No business'}</div>
                    <div className="text-[11px] text-gray-400 truncate">
                      {[user.b_industry, dist].filter(Boolean).join(' · ')}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="shrink-0 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleViewProfile(user)}
                      className="px-2.5 py-1.5 text-[11px] font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                      title="View profile"
                    >
                      Profile
                    </button>
                    <button
                      onClick={() => handleStartChat(user)}
                      className="px-2.5 py-1.5 text-[11px] font-medium text-white bg-[#00aef7] rounded-lg hover:bg-[#0095d0] transition-colors"
                      title="Start a conversation"
                    >
                      Chat
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer count */}
      {!loading && filtered.length > 0 && (
        <div className="shrink-0 px-4 py-2 border-t border-gray-100 bg-gray-50">
          <span className="text-[11px] text-gray-400">
            {filtered.length} professional{filtered.length !== 1 ? 's' : ''} within 100 miles
            {query.trim() ? ` matching "${query}"` : ''}
          </span>
        </div>
      )}
    </div>
  );
}

export default function MessageCenter() {
  const isOpen = useMessagingStore(s => s.isOpen);
  const close = useMessagingStore(s => s.close);
  const selectedThread = useMessagingStore(s => s.selectedThread);

  // Boot Matrix exactly once (hook is a singleton across the tab)
  const { isReady, error } = useMatrixClient();

  console.log('MessageCenter - isOpen:', isOpen); // DEBUG

  const modalRef = useRef(null);

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  // Lock background scrolling when chat center is open
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const onBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) close();
  };

  return (
    <div
      onMouseDown={onBackdropClick}
      className="fixed inset-0 z-[12000] flex items-center justify-center bg-black/40"
      aria-modal="true"
      role="dialog"
    >
      <Rnd
        default={{
          width: Math.min(800, Math.floor(window.innerWidth * 0.85)),
          height: Math.floor(window.innerHeight * 0.85),
          x: Math.floor((window.innerWidth - Math.min(800, Math.floor(window.innerWidth * 0.85))) / 2),
          y: Math.floor((window.innerHeight - Math.floor(window.innerHeight * 0.85)) / 2),
        }}
        minWidth={600}
        minHeight={400}
        bounds="window"
        dragHandleClassName="eh-msg-header"
        enableResizing={{
          bottomRight: true,
          bottomLeft: true,
          topRight: true,
          topLeft: true,
          left: true,
          right: true,
          bottom: true,
          top: true,
        }}
        className="rounded-2xl shadow-2xl overflow-hidden flex flex-col bg-white"
      >
        <div
          ref={modalRef}
          className="relative w-full h-full flex flex-col"
        >
          {/* Header - Keep your branding */}
          <div className="shrink-0 flex items-center justify-between px-4 sm:px-6 py-1 border-b border-[#00aef7] bg-[#00aef7] text-white cursor-move eh-msg-header">
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-semibold tracking-tight">Chats</h2>
            </div>
            <button
              onClick={close}
              className="inline-flex items-center justify-center rounded-md px-2 py-1 text-white hover:bg-white/15"
              aria-label="Close"
              title="Close"
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 min-h-0 flex">
            {/* Left Panel - Thread List */}
            <div className="w-1/3 border-r border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <input
                  type="text"
                  placeholder="Search or start a new chat"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#00aef7]"
                />
              </div>

              {/* Fast open: while Matrix is starting (first mount), show skeleton */}
              {!isReady ? (
                <div className="p-4">
                  <div className="space-y-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-lg animate-pulse">
                        <div className="w-10 h-10 rounded-full bg-gray-300"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-300 rounded w-1/2 mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <ThreadList />
              )}

              {error && (
                <div className="px-4 pb-4 text-xs text-red-600">
                  Chat connection issue — retrying…
                </div>
              )}
            </div>

            {/* Right Panel — Discover People when no thread selected, conversation view when one is */}
            <div className="w-2/3">
              {selectedThread ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center text-sm text-gray-500">
                    <div className="font-semibold text-gray-700 mb-1">Conversation View</div>
                    <div>Thread: {selectedThread}</div>
                  </div>
                </div>
              ) : (
                <DiscoverPeoplePanel />
              )}
            </div>

          </div>
        </div>
      </Rnd>
    </div>
  );
}
